import { api } from './client';
import type { Page } from './catalogs';

export type ReceiptOperationType =
  | 'FROM_BUYER' | 'FROM_BANK' | 'OTHER' | 'LOAN' | 'CARD_RECEIPT';
export type PaymentOperationType =
  | 'TO_SUPPLIER' | 'TO_BANK' | 'TAX' | 'SALARY' | 'LOAN_RETURN' | 'OTHER'
  | 'CARD_PAYMENT';

// Block 5.3a (2026-05-27): CancellableMixin state.
export type CancellableState = 'DRAFT' | 'POSTED' | 'CANCELLED';

// ----- BankReceipt -----

export interface BankReceiptListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  contractor_id: string | null;
  operation_type: string;
  amount: string;
  payment_purpose: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3a:
  state?: CancellableState;
}

export interface BankReceipt extends BankReceiptListItem {
  contract_id: string | null;
  incoming_doc_number: string | null;
  incoming_doc_date: string | null;
  payer_name: string | null;
  payer_edrpou: string | null;
  payer_iban: string | null;
  payer_bank_mfo: string | null;
  cash_flow_item_id: string | null;
  comment: string | null;
  posted_at: string | null;
  // Block 5.3a: cancellation fields.
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface BankReceiptCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  operation_type: ReceiptOperationType;
  contractor_id?: string | null;
  contract_id?: string | null;
  payer_name?: string | null;
  payer_edrpou?: string | null;
  payer_iban?: string | null;
  amount: string;
  payment_purpose?: string | null;
  comment?: string | null;
  incoming_doc_number?: string | null;
  incoming_doc_date?: string | null;
}

export type BankReceiptUpdate = Partial<BankReceiptCreate>;

export const bankReceipts = {
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<BankReceiptListItem>>('/bank-receipts', { params }).then((r) => r.data),
  get: (id: string) => api.get<BankReceipt>(`/bank-receipts/${id}`).then((r) => r.data),
  create: (payload: BankReceiptCreate) =>
    api.post<BankReceipt>('/bank-receipts', payload).then((r) => r.data),
  update: (id: string, payload: BankReceiptUpdate) =>
    api.patch<BankReceipt>(`/bank-receipts/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/bank-receipts/${id}`).then(() => undefined),
  post: (id: string) => api.post<BankReceipt>(`/bank-receipts/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<BankReceipt>(`/bank-receipts/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/bank-receipts/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// ----- BankPayment -----

export interface BankPaymentListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  contractor_id: string | null;
  operation_type: string;
  amount: string;
  payment_purpose: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  project_id?: string | null;
  // Block 5.3a:
  state?: CancellableState;
}

export interface BankPayment extends BankPaymentListItem {
  contract_id: string | null;
  employee_id: string | null;
  recipient_name: string | null;
  recipient_edrpou: string | null;
  recipient_iban: string | null;
  recipient_bank_mfo: string | null;
  cash_flow_item_id: string | null;
  comment: string | null;
  posted_at: string | null;
  // Block 5.3a: cancellation fields.
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface BankPaymentCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  bank_account_id: string;
  operation_type: PaymentOperationType;
  contractor_id?: string | null;
  contract_id?: string | null;
  employee_id?: string | null;
  recipient_name?: string | null;
  recipient_edrpou?: string | null;
  recipient_iban?: string | null;
  amount: string;
  payment_purpose?: string | null;
  comment?: string | null;
}

export type BankPaymentUpdate = Partial<BankPaymentCreate>;

export const bankPayments = {
  list: (params?: { organization_id?: string; contractor_id?: string; posted?: boolean; limit?: number }) =>
    api.get<Page<BankPaymentListItem>>('/bank-payments', { params }).then((r) => r.data),
  get: (id: string) => api.get<BankPayment>(`/bank-payments/${id}`).then((r) => r.data),
  create: (payload: BankPaymentCreate) =>
    api.post<BankPayment>('/bank-payments', payload).then((r) => r.data),
  update: (id: string, payload: BankPaymentUpdate) =>
    api.patch<BankPayment>(`/bank-payments/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/bank-payments/${id}`).then(() => undefined),
  post: (id: string) => api.post<BankPayment>(`/bank-payments/${id}/post`).then((r) => r.data),
  unpost: (id: string) => api.post<BankPayment>(`/bank-payments/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/bank-payments/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
